import { ApolloProvider } from '@apollo/client';
import clsx from 'clsx';
import { config } from 'config';
import * as lscache from 'lscache';
import { SSOResponseCode } from 'models/ssoTypes';
import { TitleSuffix } from 'modules/UIContext/constants';
import { ErrorBoundary } from 'modules/components/ErrorBoundary/ErrorBoundary';
import { NotFoundStation } from 'modules/components/NotFoundStation/NotFoundStation';
import { SignIn } from 'modules/components/SignIn/SignIn';
import { SignInError } from 'modules/components/SignInError/SignInError';
import { Slide } from 'modules/components/Slide/Slide';
import { SsoSignIn } from 'modules/components/SsoSignIn/SsoSignIn';
import { StationRoot } from 'modules/components/StationRoot/StationRoot';
import { TenantSelector } from 'modules/components/TenantSelector/TenantSelector';
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { SignInMethod } from '../../models/signInMethod';
import { authStateStore } from '../../state-store';
import { UIContextProvider } from '../UIContext/UIContext';
import { apolloClient } from '../apolloClient';
import { Header, useBreadcrumb } from '../components/Header/Header';
import { Routes as routes } from '../routes';
import classes from './IdServiceApp.module.scss';

export const IdServiceApp: React.FC = () => {
  // A well-known key containing which sign-in method to use will allow the user to override the default sign-in method.
  // This may be used in troubleshooting scenarios.
  const userOverriddenSignInMethod = (() => {
    switch (window.localStorage.getItem('AXINOM_SIM_OVERRIDE')) {
      case SignInMethod.SSO:
        return SignInMethod.SSO;
      case SignInMethod.DIRECT:
        return SignInMethod.DIRECT;
      default:
        return undefined;
    }
  })();

  const signInMethod =
    userOverriddenSignInMethod ??
    (config.defaultSignInMethod as SignInMethod) ??
    SignInMethod.DIRECT;
  lscache.set('signInMethod', signInMethod);

  const ssoContext = authStateStore.useState('ssoContext');
  // React to changes of the accessToken (i.e. signIn, changePassword)
  authStateStore.useState('accessToken');

  const currentTenantId = lscache.get('currentTenantId');
  const accessToken = lscache.get('accessToken');

  const [showHeaderBar, setShowHeaderBar] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      authStateStore.setState({
        accessToken: accessToken,
        tenantId: currentTenantId,
        tenantName: lscache.get('currentTenantName'),
        tenantAdminEmail: lscache.get('tenantAdminEmail'),
        tenantAdminName: lscache.get('tenantAdminName'),
      });

      const signInProcessCompleted = ((): boolean => {
        if (signInMethod === SignInMethod.DIRECT) {
          return accessToken !== null;
        } else {
          // This would be the (signInMethod === SignInMethod.SSO) branch
          return ssoContext.signInStatus === true && currentTenantId !== null;
        }
      })();
      setShowHeaderBar(signInProcessCompleted);
    })();
  }, [currentTenantId, signInMethod, accessToken, ssoContext.signInStatus]);

  const renderComponentForSSO = (component: React.FC): (() => JSX.Element) => {
    return renderComponent(
      ssoContext.ssoError !== undefined &&
        ssoContext.ssoError.code !== SSOResponseCode.NOT_LOGGED_IN &&
        ssoContext.ssoError?.message !== undefined
        ? SignInError
        : ssoContext.signInStatus === true && currentTenantId === null
        ? TenantSelector
        : ssoContext.signInStatus === true && currentTenantId !== null
        ? component
        : SsoSignIn,
    );
  };

  return (
    <UIContextProvider>
      <BrowserRouter>
        <ApolloProvider client={apolloClient()}>
          <div className={clsx(classes.container)}>
            <div>{showHeaderBar && <Header />}</div>
            <div className={classes.main}>
              <SwitchWithTransition>
                {routes?.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    render={
                      signInMethod === SignInMethod.SSO
                        ? renderComponentForSSO(route.component)
                        : signInMethod === SignInMethod.DIRECT
                        ? renderComponent(
                            accessToken !== null &&
                              lscache.get('requirePasswordChange') !== true
                              ? route.component
                              : SignIn, // ChangePassword state is handled inside the SignIn component
                          )
                        : renderComponent(SignInError)
                    }
                    exact
                  />
                ))}
                <Route
                  render={
                    signInMethod === SignInMethod.SSO
                      ? renderComponentForSSO(NotFoundStation)
                      : renderComponent(NotFoundStation)
                  }
                />
              </SwitchWithTransition>
            </div>
          </div>
        </ApolloProvider>
      </BrowserRouter>
    </UIContextProvider>
  );
};

// TODO:
// 1. Move the `SwitchWithTransition` component inside a new Layout Component (similar to Portal package)
// 2. Move the `Header` component inside the same
// 3. Adopt name names used in Portal package (i.e. `AnimatedRoutes` instead of `SwitchWithTransition`)
// 4. Evaluate if `NotFoundStation` from Portal package can be adopted as well
const SwitchWithTransition: React.FC = ({ children }) => {
  const breadcrumbs = useBreadcrumb();
  const location = useLocation();

  window.document.title = TitleSuffix;

  return (
    <Slide breadcrumbs={breadcrumbs}>
      <Switch location={location}>{children}</Switch>
    </Slide>
  );
};

const renderComponent = (Component: React.FC): (() => JSX.Element) => {
  const renderFn = (): JSX.Element => (
    <StationRoot>
      <ErrorBoundary>
        <Component />
      </ErrorBoundary>
    </StationRoot>
  );
  return renderFn;
};
